import React, { useState, useEffect, useCallback } from "react";
import { X, CheckSquare, BookOpen, Clock, FileText, Upload, Award, Send, ArrowLeft, Video, Paperclip, Maximize2, Minimize2, Trash2, Pencil, UserPlus, Flag } from "lucide-react";
import api from "../api";
import MessageModal from "./MessageModal";
import MessageDetails from "./MessageDetails";
import GradeManagement from "./GradeManagement";
import ClassReportsModal from "./ClassReportsModal";
import MeetNamePrompt from "./MeetNamePrompt";

const DailyMenu = ({ date, role, eventToEdit, onClose }) => {
  const formattedDate = date
    ? date.toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const [activeAction, setActiveAction] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    diplomatura: eventToEdit ? eventToEdit.extendedProps?.diploma || "" : "",
    materia: eventToEdit ? eventToEdit.extendedProps?.subject || "" : "",
    email: "",
    teacherEmail: eventToEdit
      ? eventToEdit.extendedProps?.teacher_email || ""
      : "",
    fecha: eventToEdit
      ? eventToEdit.extendedProps?.date ||
        (eventToEdit.start instanceof Date ? eventToEdit.start.toISOString().split("T")[0] : "")
      : date ? (date instanceof Date ? date.toISOString().split("T")[0] : date) : "",
    horaInicio: eventToEdit
      ? eventToEdit.extendedProps?.start_time?.substring(0, 5) ||
        new Date(eventToEdit.start).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "09:00",
    horaFin: eventToEdit
      ? eventToEdit.extendedProps?.end_time?.substring(0, 5) ||
        new Date(eventToEdit.end).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "12:00",
    fechaFin: "",
    frecuencia: "",
    modality: eventToEdit ? eventToEdit.extendedProps?.modality || "VI" : "VI",
    meet_link: eventToEdit ? eventToEdit.extendedProps?.meet_link || "" : "",
    series_meet_link: "",
    content_blocks: eventToEdit
      ? eventToEdit.extendedProps?.content_blocks || ""
      : "",
    series_content_blocks: "",
    series_id: eventToEdit ? eventToEdit.extendedProps?.series_id || "" : "",
    editScope: "single",
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [seriesFiles, setSeriesFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [seriesId, setSeriesId] = useState(null);
  const [newBlock, setNewBlock] = useState("");
  const [showScopePrompt, setShowScopePrompt] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [pendingAction, setPendingAction] = useState("add"); // 'add', 'edit', 'delete'
  const [targetBlockIndex, setTargetBlockIndex] = useState(null);
  const [showAttendanceList, setShowAttendanceList] = useState(false);
  const [classAttendance, setClassAttendance] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [meetJoinUrl, setMeetJoinUrl] = useState(
    eventToEdit?.extendedProps?.meet_join_url || null
  );
  const [meetLoading, setMeetLoading] = useState(false);
  const [showMeetPrompt, setShowMeetPrompt] = useState(false);

  // Logic for Attendance
  const now = new Date();
  const isOngoing =
    eventToEdit &&
    !eventToEdit.extendedProps?.isMessage &&
    now >= new Date(eventToEdit.start) &&
    now <= new Date(eventToEdit.end);

  // El botón de Meet se habilita un poco antes de que empiece la clase
  // (15 min de margen para entrar y probar audio/video) y hasta que termina.
  const MEET_EARLY_JOIN_MINUTES = 15;
  const canJoinMeet =
    eventToEdit &&
    !eventToEdit.extendedProps?.isMessage &&
    (() => {
      const start = new Date(eventToEdit.start);
      start.setMinutes(start.getMinutes() - MEET_EARLY_JOIN_MINUTES);
      return now >= start && now <= new Date(eventToEdit.end);
    })();

  const [hasMarkedAttendance, setHasMarkedAttendance] = useState(false);

  useEffect(() => {
    if (eventToEdit && !eventToEdit.extendedProps?.isMessage && role === "AL") {
      checkAttendance();
    }
  }, [eventToEdit, role]);

  const checkAttendance = async () => {
    try {
      const userEmail = sessionStorage.getItem("userEmail");
      const res = await api.get(
        `attendance/?class_event=${eventToEdit.id.replace("class-", "")}&student__email=${userEmail}`,
      );
      if (res.data.some((a) => a.is_present)) {
        setHasMarkedAttendance(true);
      }
    } catch (err) {
      console.error("Error checking attendance:", err);
    }
  };

  const fetchClassAttendance = async () => {
    setLoadingAttendance(true);
    try {
      const classId = eventToEdit.id.replace("class-", "");
      const res = await api.get(`classes/${classId}/attendance-list/`);
      setClassAttendance(res.data);
      setShowAttendanceList(true);
    } catch (err) {
      console.error("Error fetching class attendance:", err);
      alert("No se pudo cargar la lista de presentes.");
    } finally {
      setLoadingAttendance(false);
    }
  };

  // Pre-fill form if editing an event (only for message view or admin edit)
  useEffect(() => {
    if (eventToEdit) {
      const extProps = eventToEdit.extendedProps;

      if (extProps.isMessage) {
        setActiveAction({ id: "msg-view", title: "Ver Mensaje", isForm: true });
      } else if (role === "CD") {
        setActiveAction({
          id: "c-1",
          title: "Modificar Programación de Clase",
          icon: <FileText size={20} />,
          color: "primary",
          isForm: true,
        });
      } else if (role === "DO" || role === "PR") {
        // Professors see a different form for content/link editing
        setActiveAction({
          id: "pr-edit",
          title: "Editar Contenido de Clase",
          icon: <FileText size={20} />,
          color: "warning",
          isForm: true,
        });
      } else {
        setActiveAction(null);
      }
    }
  }, [eventToEdit, role]);

  // Fetch existing documents for this class
  const fetchExistingDocuments = useCallback(async () => {
    if (eventToEdit && !eventToEdit.extendedProps?.isMessage) {
      const classId = eventToEdit.id.replace("class-", "");
      try {
        const res = await api.get(`class-documents/?class_event=${classId}`);
        setExistingDocuments(res.data);
      } catch (err) {
        console.error("Error fetching class documents:", err);
      }
    }
  }, [eventToEdit]);

  useEffect(() => {
    fetchExistingDocuments();
  }, [fetchExistingDocuments]);

  // Datos simulados (mock) que vendrían de la BD (leemos del storage para persistencia en frontend)
  const [allDiplomaturas, setAllDiplomaturas] = useState([]);
  const storedUserRole = sessionStorage.getItem("userRole");
  const userCoordinatedDiplos =
    JSON.parse(sessionStorage.getItem("userCoordinatedDiplos")) || [];

  useEffect(() => {
    fetchDiplomaturas();
  }, []);

  const fetchDiplomaturas = async () => {
    try {
      const res = await api.get("diplomas/");
      setAllDiplomaturas(res.data);
    } catch (err) {
      console.error("Error fetching diplomaturas:", err);
    }
  };

  const diplomaturasFiltradas = allDiplomaturas;

  const currentUser = sessionStorage.getItem('userEmail') || "usuario@admin.com";
  const selectedDiploObj = allDiplomaturas.find(
    (d) => d.id.toString() === formData.diplomatura.toString(),
  );
  const materiasFiltradas = selectedDiploObj?.subjects || [];
  const selectedMateriaObj = materiasFiltradas.find(
    (m) => m.id.toString() === formData.materia.toString(),
  );
  
  const availableTeachers = selectedMateriaObj?.teachers_data || [];

  // Definir las acciones disponibles según el rol
  const getActionsForRole = () => {
    let roleActions = [];

    switch (role) {
      case "AL": // Alumno
        if (eventToEdit && !eventToEdit.extendedProps?.isMessage) {
          const status = hasMarkedAttendance
            ? "success"
            : isOngoing
              ? "success"
              : "secondary";
          roleActions.push(
            {
              id: "al-1",
              title: hasMarkedAttendance
                ? "Asistencia Registrada"
                : isOngoing
                  ? "Dar Presente"
                  : "Fuera de Horario",
              icon: <CheckSquare size={20} />,
              color: status,
              disabled: !isOngoing || hasMarkedAttendance,
            },
            {
              id: "al-2",
              title: "Ver Material de Clase",
              icon: <BookOpen size={20} />,
              color: "info",
            },
          );
        }
        break;
      case "CD": // Coordinadores
        roleActions.push({
          id: "c-1",
          title: "Crear Clase",
          icon: <FileText size={20} />,
          color: "primary",
          isForm: true,
        });
        break;
      case "DO": // Docente
      case "PR": // Profesor
        if (eventToEdit && !eventToEdit.extendedProps?.isMessage) {
          roleActions.push({
            id: "pr-edit",
            title: "Editar Contenido de Clase",
            icon: <FileText size={20} />,
            color: "warning",
            isForm: true,
          });
        }
        break;
      case "RM": // Referente Municipal
        // RM previously had "Ver Reporte Asistencia" here, now removed as per request
        break;
    }

    // Acciones universales (Siempre disponibles para todos los roles)
    roleActions.push({
      id: "msg-all",
      title: "Dejar un Mensaje / Tarea",
      icon: <Send size={20} />,
      color: "success",
      isForm: true,
    });

    roleActions.push({
      id: "event-general",
      title: "Cargar Evento General (Feriado, etc.)",
      icon: <Flag size={20} />,
      color: "info",
      isForm: true,
    });

    return roleActions;
  };

  const actions = getActionsForRole();

  const handleActionClick = async (action) => {
    if (action.isForm) {
      setActiveAction(action);
      return;
    }

    if (action.id === "al-1") {
      if (action.disabled) return;

      try {
        const userEmail = sessionStorage.getItem("userEmail");
        await api.post("attendance/", {
          class_event: eventToEdit.id.replace("class-", ""),
        });
      setHasMarkedAttendance(true);
      } catch (err) {
        console.error("Error marking attendance:", err);
      }
    } else if (action.id === "al-2") {
      setActiveAction(action);
    } else if (action.id === "rm-1") {
      setActiveAction(action);
    } else if (action.id === "do-grades") {
      setShowGradeModal(true);
    } else {
      console.log(`Acción "${action.title}" en desarrollo...`);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (activeAction.id === "c-1") {
      const [startHour, startMinute] = formData.horaInicio
        .split(":")
        .map(Number);
      const [endHour, endMinute] = formData.horaFin.split(":").map(Number);

      if (eventToEdit) {
        // When editing, don't require strict local lookup — use fallback from event
        await handleEditClass(startHour, startMinute, endHour, endMinute);
      } else {
        // When creating, we do need valid diplomatura/materia selection
        const selectedMateriaObj = materiasFiltradas.find(
          (m) => m.id.toString() === formData.materia.toString(),
        );
        const selectedDiploObj = allDiplomaturas.find(
          (d) => d.id.toString() === formData.diplomatura,
        );
        if (!selectedDiploObj || !selectedMateriaObj) {
          alert("Por favor seleccione una diplomatura y materia válidas.");
          return;
        }
        await handleCreateClass(startHour, startMinute, endHour, endMinute);
      }
    } else if (activeAction.id === "pr-edit") {
      await handleProfessorSubmit();
    } else if (
      activeAction.id === "pr-2" ||
      activeAction.id === "ac-1" ||
      activeAction.id === "al-enrol"
    ) {
      console.log("Acción procesada exitosamente.");
      setActiveAction(null);
      onClose(false);
    }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfessorSubmit = async (scope = "single") => {
    try {
      setIsUploading(true);
      const classId = eventToEdit.id.replace("class-", "");
      const actionType = pendingAction;
      
      let updatedBlocks = "";
      const currentBlocksArray = formData.content_blocks ? formData.content_blocks.split("\n\n") : [];

      if (actionType === "add") {
        updatedBlocks = newBlock 
          ? (formData.content_blocks ? formData.content_blocks + "\n\n" + newBlock : newBlock)
          : formData.content_blocks;
      } else if (actionType === "edit" && editingIndex !== null) {
        const newArray = [...currentBlocksArray];
        newArray[editingIndex] = newBlock;
        updatedBlocks = newArray.join("\n\n");
      } else if (actionType === "delete" && targetBlockIndex !== null) {
        const newArray = currentBlocksArray.filter((_, i) => i !== targetBlockIndex);
        updatedBlocks = newArray.join("\n\n");
      }

      const patchData = {
        content_blocks: updatedBlocks,
        editScope: scope
      };

      await api.patch(`classes/${classId}/`, patchData);

      // Handle attachment re-indexing/deletion on block delete
      if (actionType === "delete" && targetBlockIndex !== null) {
        const promises = [];
        for (const doc of existingDocuments) {
          if (doc.block_index === targetBlockIndex) {
            // Delete attachments linked to the deleted block
            promises.push(api.delete(`class-documents/${doc.id}/`));
          } else if (doc.block_index > targetBlockIndex) {
            // Shift indices of subsequent attachments
            promises.push(api.patch(`class-documents/${doc.id}/`, { block_index: doc.block_index - 1 }));
          }
        }
        if (promises.length > 0) {
          await Promise.all(promises);
          // Wait briefly for server to process before refresh if needed, 
          // but usually useEffect will handle it on re-render.
        }
      }
      
      setFormData({ ...formData, content_blocks: updatedBlocks });
      setNewBlock("");
      setEditingIndex(null);
      setTargetBlockIndex(null);
      setPendingAction("add");
      setShowScopePrompt(false);

      // Upload files if any (associated with the block)
      let targetIdx = actionType === "add" ? currentBlocksArray.length : editingIndex;
      
      if (actionType !== "delete") {
        for (const fileObj of uploadedFiles) {
          const fd = new FormData();
          fd.append("class_event", classId);
          fd.append("name", fileObj.name);
          fd.append("file", fileObj);
          fd.append("propagate_to_series", scope === "series" ? "true" : "false");
          fd.append("block_index", targetIdx);
          await api.post("class-documents/", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      }
      setUploadedFiles([]);
      await fetchExistingDocuments();
      
      console.log("Acción en bloque completada exitosamente.");
      if (scope === "series") {
          alert("Cambios aplicados a toda la serie de clases.");
      }
      setActiveAction(null);
      onClose(true);
    } catch (err) {
      console.error("Error updating professor content:", err);
      alert("Error al actualizar el contenido.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateClass = async (
    startHour,
    startMinute,
    endHour,
    endMinute,
  ) => {
    try {
      const seriesId = `series-${Date.now()}`;
      let currentDate = new Date(date);
      currentDate.setHours(startHour, startMinute, 0, 0);

      const endDate =
        formData.frecuencia && formData.fechaFin
          ? new Date(formData.fechaFin)
          : new Date(currentDate);
      endDate.setHours(23, 59, 59, 999);

      const classesToCreate = [];
      const start_time = `${startHour.toString().padStart(2, "0")}:${startMinute.toString().padStart(2, "0")}`;
      const end_time = `${endHour.toString().padStart(2, "0")}:${endMinute.toString().padStart(2, "0")}`;

      const selectedMateriaObj = materiasFiltradas.find(
        (m) => m.id.toString() === formData.materia.toString(),
      );

      while (currentDate <= endDate) {
        classesToCreate.push({
          subject: formData.materia, 
          teacher: availableTeachers.find(
            (t) => t.email === formData.teacherEmail,
          )?.id,
          title: `${selectedMateriaObj?.name || formData.materia} - ${selectedDiploObj.name} (${selectedDiploObj.municipio})`, 
          date: currentDate.getFullYear() + "-" + 
                String(currentDate.getMonth() + 1).padStart(2, '0') + "-" + 
                String(currentDate.getDate()).padStart(2, '0'),
          start_time,
          end_time,
          modality: formData.modality,
        });

        if (!formData.frecuencia) break;
        if (formData.frecuencia === "diaria")
          currentDate.setDate(currentDate.getDate() + 1);
        else if (formData.frecuencia === "semanal")
          currentDate.setDate(currentDate.getDate() + 7);
        else if (formData.frecuencia === "quincenal")
          currentDate.setDate(currentDate.getDate() + 14);
        else if (formData.frecuencia === "mensual")
          currentDate.setMonth(currentDate.getMonth() + 1);
      }

      // In a real production environment we would use a bulk create endpoint.
      // For this stabilization, we'll loop or just create the one.
      for (const cls of classesToCreate) {
        await api.post("classes/", {
          ...cls,
          series_id: seriesId,
        });
      }

      console.log(
        `${classesToCreate.length} Clase(s) programada(s) exitosamente.`,
      );
      console.log("DEBUG: Calling onClose(true)");
      setActiveAction(null);
      onClose(true);
    } catch (err) {
      console.error("Error creating class:", err);
      if (err.response) {
        console.error("Server Response Error Details:", JSON.stringify(err.response.data, null, 2));
      }
      alert("Error al programar la clase. Revisá la consola para más detalles.");
    }
  };

  const handleEditClass = async (
    startHour,
    startMinute,
    endHour,
    endMinute,
  ) => {
    try {
      const start_time = `${startHour.toString().padStart(2, "0")}:${startMinute.toString().padStart(2, "0")}`;
      const end_time = `${endHour.toString().padStart(2, "0")}:${endMinute.toString().padStart(2, "0")}`;

      // Resolve IDs directly from formData or fallback to event props
      const subjectId = formData.materia || eventToEdit?.extendedProps?.subject;
      const teacherId =
        availableTeachers.find((t) => t.email === formData.teacherEmail)?.id ||
        eventToEdit?.extendedProps?.teacher;

      const payload = {
        date: formData.fecha,
        start_time,
        end_time,
        modality: formData.modality,
        editScope: formData.editScope,
      };

      // Only include subject/teacher if they resolved
      if (subjectId) payload.subject = subjectId;
      if (teacherId) payload.teacher = teacherId;

      await api.patch(
        `classes/${eventToEdit.id.replace("class-", "")}/`,
        payload,
      );

      console.log("Clase modificada exitosamente.");
      setActiveAction(null);
      onClose(true);
    } catch (err) {
      console.error("Error updating class:", err);
      alert(
        "Error al modificar la clase. Revisá la consola para más detalles.",
      );
    }
  };

  const handleDeleteClass = async () => {
    let finalScope = formData.editScope;
    // Enhanced detection: check multiple sources and naming conventions
    const seriesId = eventToEdit?.extendedProps?.series_id || 
                     eventToEdit?.extendedProps?.seriesId || 
                     formData.series_id;

    console.log("DEBUG: handleDeleteClass - detected seriesId:", seriesId);
    console.log("DEBUG: eventToEdit.extendedProps:", JSON.stringify(eventToEdit?.extendedProps));

    if (seriesId && seriesId !== "") {
      const confirmDeleteSeries = window.confirm(
        "Esta clase es parte de una serie recurrente.\n\n" +
          "¿Desea eliminar TODA LA SERIE de clases futuras?\n\n" +
          "ACEPTAR: Elimina esta y todas las clases siguientes de la serie.\n" +
          "CANCELAR: Elimina SOLO esta clase individual.",
      );

      if (confirmDeleteSeries) {
        finalScope = "series";
      } else {
        // Segundo paso: confirmar que realmente quiere borrar solo esta
        if (
          !window.confirm(
            "¿Confirma que desea eliminar SOLO esta clase individual?",
          )
        )
          return;
        finalScope = "single";
      }
    } else {
      if (!window.confirm("¿Está seguro de que desea eliminar esta clase?"))
        return;
    }

    try {
      const classId = eventToEdit.id.replace("class-", "");
      
      // Use query param instead of data body for DELETE to ensure compatibility
      await api.delete(`classes/${classId}/?editScope=${finalScope}`);
      
      console.log(`Clase ${finalScope === 'series' ? 'y serie' : 'individual'} eliminada exitosamente.`);
      setActiveAction(null);
      onClose(true);
    } catch (err) {
      console.error("Error deleting class:", err);
      alert("Error al eliminar la clase.");
    }
  };


  const mockStudents = [
    { id: 101, name: "Juan Pérez", email: "juan@estudiante.com" },
    { id: 102, name: "María García", email: "maria@estudiante.com" },
    { id: 103, name: "Carlos López", email: "carlos@estudiante.com" },
    { id: 104, name: "Ana Belén", email: "ana@estudiante.com" },
  ];

  const classId = eventToEdit?.id?.replace("class-", "");

  const handleCreateMeet = async () => {
    setMeetLoading(true);
    try {
      const res = await api.post(`classes/${classId}/create-meet/`);
      setMeetJoinUrl(res.data.meet_join_url);
    } catch (e) {
      alert("Error al crear la sala de Google Meet. Verificá la configuración.");
    } finally {
      setMeetLoading(false);
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 10000 }}
    >
      <div
        className={`modal-dialog modal-dialog-centered modal-dialog-scrollable ${isFullscreen ? "modal-fullscreen" : ""}`}
      >
        <div className="modal-content glassmorphism">
          <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
            <h5 className="modal-title section-title fs-4 mb-0 text-capitalize">
              {showAttendanceList ? "Asistencia de Clase" : (activeAction ? activeAction.title : formattedDate)}
            </h5>
            <div className="d-flex align-items-center gap-2 ms-auto">
              <button
                type="button"
                className="btn-glass p-1 d-flex align-items-center justify-content-center"
                onClick={() => setIsFullscreen((f) => !f)}
                title={
                  isFullscreen ? "Contraer" : "Ampliar a pantalla completa"
                }
                style={{ width: "32px", height: "32px", borderRadius: "8px" }}
              >
                {isFullscreen ? (
                  <Minimize2 size={16} />
                ) : (
                  <Maximize2 size={16} />
                )}
              </button>
              <button
                type="button"
                className="btn-close-premium"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="modal-body">
            {showAttendanceList ? (
              <AttendanceListView 
                students={classAttendance} 
                loading={loadingAttendance} 
                onBack={() => setShowAttendanceList(false)} 
              />
            ) : (
              <>
                {/* --- BOTONES SUPERIORES (Solo cuando hay clase seleccionada) --- */}
                {eventToEdit && !eventToEdit.extendedProps?.isMessage && role !== "AL" && (
                  <div className="mb-4 d-flex flex-column gap-2">
                    {/* Sala Virtual Google Meet — solo visible cerca del horario de la clase */}
                    {canJoinMeet && (meetJoinUrl ? (
                      <a
                        href={meetJoinUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-success w-100 py-3 d-flex align-items-center justify-content-center gap-3 fw-bold border-0 shadow-sm text-decoration-none"
                        style={{ borderRadius: "12px" }}
                      >
                        <Video size={22} />
                        <div className="text-start">
                          <div className="fw-bold">Iniciar Google Meet</div>
                          <div className="x-small opacity-75">Abre la sala en una nueva pestaña</div>
                        </div>
                      </a>
                    ) : (
                      <button
                        className="btn btn-success w-100 py-3 d-flex align-items-center justify-content-center gap-3 fw-bold border-0 shadow-sm"
                        onClick={handleCreateMeet}
                        disabled={meetLoading}
                        style={{ borderRadius: "12px" }}
                      >
                        <Video size={22} />
                        <div className="text-start">
                          <div className="fw-bold">{meetLoading ? "Creando sala..." : "Crear sala Google Meet"}</div>
                          <div className="x-small opacity-75">Genera un link para la clase de hoy</div>
                        </div>
                      </button>
                    ))}
                    {/* Lista de presentes */}
                    <button
                      onClick={fetchClassAttendance}
                      className="btn btn-primary w-100 py-3 rounded-xl shadow-sm d-flex align-items-center justify-content-center gap-3 hover-lift border-0"
                      style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
                    >
                      <UserPlus size={20} />
                      <div className="text-start">
                        <div className="fw-bold">Ver Lista de Presentes</div>
                        <div className="x-small opacity-75">Consultar alumnos que marcaron asistencia hoy</div>
                      </div>
                    </button>
                  </div>
                )}

                {/* Botón unirse para alumnos — solo visible cerca del horario de la clase */}
                {eventToEdit && !eventToEdit.extendedProps?.isMessage && role === "AL" && canJoinMeet && (
                  <div className="mb-4">
                    {meetJoinUrl ? (
                      <button
                        type="button"
                        onClick={() => setShowMeetPrompt(true)}
                        className="btn w-100 py-3 d-flex align-items-center justify-content-center gap-3 fw-bold border-0 shadow-sm"
                        style={{ background: "linear-gradient(135deg, #10b981, #059669)", borderRadius: "12px" }}
                      >
                        <Video size={22} />
                        <div className="text-start">
                          <div className="fw-bold">Unirse a la Clase Virtual</div>
                          <div className="x-small opacity-75">Abre Google Meet en una nueva pestaña</div>
                        </div>
                      </button>
                    ) : (
                      <div
                        className="w-100 py-3 d-flex align-items-center justify-content-center gap-3 text-muted border rounded"
                        style={{ borderRadius: "12px", background: "#f8f9fa" }}
                      >
                        <Video size={22} />
                        <div className="text-start">
                          <div className="fw-bold">Sala virtual no disponible aún</div>
                          <div className="x-small">El docente aún no creó la sala de Google Meet</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {eventToEdit && !eventToEdit.extendedProps?.isMessage && (
              <div className={`class-details-header mb-4 p-3 rounded bg-light border-start border-4 ${eventToEdit.extendedProps.modality === "PR" ? "border-warning" : "border-primary"} text-start`} style={{ borderColor: eventToEdit.extendedProps.modality === "PR" ? "#ff6b00" : "#3b82f6" }}>
                <div className="d-flex align-items-center gap-2 mb-2 text-primary fw-bold">
                  <BookOpen size={18} /> Detalle de la Clase
                </div>
                <div className="row g-2 small">
                  <div className="col-12">
                    <strong>Diplomatura:</strong>{" "}
                    {eventToEdit.extendedProps.diploma_name} ({eventToEdit.extendedProps.diploma_municipio || 'Sin sede'})
                  </div>
                  <div className="col-12">
                    <strong>Materia:</strong>{" "}
                    {eventToEdit.extendedProps.subject_name}
                  </div>
                  <div className="col-md-6">
                    <strong>Docente:</strong>{" "}
                    {eventToEdit.extendedProps.teacher_name ||
                      eventToEdit.extendedProps.teacher_email ||
                      "Sin asignar"}
                  </div>
                  <div className="col-md-6">
                    <strong>Horario:</strong>{" "}
                    {eventToEdit.extendedProps.start_time?.substring(0, 5) ||
                      eventToEdit.start?.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}{" "}
                    -{" "}
                    {eventToEdit.extendedProps.end_time?.substring(0, 5) ||
                      eventToEdit.end?.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                  </div>
                  <div className="col-md-6">
                    <strong>Modalidad:</strong>{" "}
                    {eventToEdit.extendedProps.modality === "VI"
                      ? "Virtual"
                      : "Presencial"}
                  </div>
                  {eventToEdit.extendedProps.meet_link && (
                    <div className="col-12 mt-2">
                      <a
                        href={
                          eventToEdit.extendedProps.meet_link.startsWith("http")
                            ? eventToEdit.extendedProps.meet_link
                            : `https://${eventToEdit.extendedProps.meet_link}`
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1 text-white border-0"
                        style={{ backgroundColor: "#8b5cf6" }}
                      >
                        <Video size={14} /> Unirse a la Clase Virtual
                      </a>
                    </div>
                  )}


                  {/* Read-only content and documents for non-editors: students, ACO, CO */}
                  {role !== "DO" && role !== "PR" && role !== "CD" && (
                    <div className="col-12 mt-3">
                      {eventToEdit.extendedProps.content_blocks && (
                        <div className="mb-4">
                          {eventToEdit.extendedProps.content_blocks.split("\n\n").map((block, idx) => (
                            <div key={idx} className="mb-3 p-3 bg-white border-start border-4 border-info rounded shadow-sm">
                              <p className="mb-2 text-dark" style={{ whiteSpace: "pre-wrap" }}>
                                {block}
                              </p>
                              
                              {/* Adjuntos acoplados a este bloque */}
                              {existingDocuments.filter(d => d.block_index === idx).map(doc => (
                                <a
                                  key={doc.id}
                                  href={doc.file_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="d-flex align-items-center gap-2 py-1 small text-decoration-none text-info hover-underline"
                                >
                                  <Paperclip size={12} />
                                  {doc.name}
                                </a>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Otros archivos (sin bloque asignado) */}
                      {existingDocuments.filter(d => d.block_index === null || d.block_index === undefined).length > 0 && (
                        <div className="mt-3">
                          <strong className="d-block mb-2 small text-muted text-uppercase">Archivos Adicionales:</strong>
                          <div className="list-group list-group-flush border rounded-3 overflow-hidden">
                            {existingDocuments.filter(d => d.block_index === null || d.block_index === undefined).map((doc) => (
                              <a
                                key={doc.id}
                                href={doc.file_url}
                                target="_blank"
                                rel="noreferrer"
                                className="list-group-item list-group-item-action d-flex align-items-center gap-2 py-2 small"
                              >
                                <Paperclip size={13} className="text-primary flex-shrink-0" />
                                {doc.name}
                                {doc.propagated_to_series && (
                                  <span className="badge bg-primary-subtle text-primary border border-primary-subtle x-small ms-auto" style={{fontSize: '0.6rem'}}>
                                    Serie
                                  </span>
                                )}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {!activeAction ? (
              <>
                <p className="text-muted mb-4">
                  Seleccione una acción para este día (Rol Activo:{" "}
                  <strong>{role || "Sin Rol"}</strong>)
                </p>

                <div className="d-grid gap-3">
                  {actions.map((action) => (
                    <button
                      key={action.id}
                      className={`btn btn-${action.color} text-white d-flex align-items-center justify-content-center flex-column p-4 h-100 ${action.disabled ? "opacity-50" : ""}`}
                      onClick={() => handleActionClick(action)}
                      disabled={action.disabled}
                    >
                      <span className="me-3">{action.icon}</span>
                      <span className="fw-semibold">{action.title}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="action-form-container">
                {activeAction.id === "msg-view" ? (
                  <MessageDetails
                    message={eventToEdit.extendedProps.msgData}
                    userRole={role}
                    onClose={(refresh) => {
                      if (refresh) onClose(true);
                      setActiveAction(null);
                    }}
                    subjectName={eventToEdit.extendedProps.subject_name}
                  />
                ) : activeAction.id === "msg-all" ? (
                  <MessageModal date={date} onClose={onClose} />
                ) : activeAction.id === "event-general" ? (
                  <MessageModal date={date} onClose={onClose} initialType="evento" />
                ) : activeAction.id === "al-2" ? (
                  <div className="text-start">
                    <div className="d-flex align-items-center gap-2 mb-4 p-3 bg-info bg-opacity-10 text-info rounded-3 border border-info border-opacity-25">
                      <BookOpen size={24} />
                      <h6 className="mb-0 fw-bold">Materiales de la Clase</h6>
                    </div>
                    
                    {existingDocuments.length > 0 ? (
                      <div className="list-group list-group-flush border rounded-4 shadow-sm overflow-hidden mb-4">
                        {existingDocuments.map((doc) => (
                          <a
                            key={doc.id}
                            href={doc.file_url}
                            target="_blank"
                            rel="noreferrer"
                            className="list-group-item list-group-item-action d-flex align-items-center gap-3 py-3 px-4 hover-lift"
                          >
                            <div className="p-2 bg-light rounded-circle">
                              <Paperclip size={18} className="text-primary" />
                            </div>
                            <div className="flex-grow-1">
                              <div className="fw-bold text-dark">{doc.name}</div>
                              <div className="text-muted small">Haz clic para ver o descargar</div>
                            </div>
                            {doc.propagated_to_series && (
                              <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">
                                General
                              </span>
                            )}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center p-5 bg-light rounded-4 mb-4 border border-dashed text-muted">
                        <Paperclip size={32} className="mb-3 opacity-25" />
                        <p className="mb-0">No se han subido materiales adicionales para esta clase.</p>
                      </div>
                    )}
                    
                    <button
                      type="button"
                      className="btn btn-secondary w-100 py-3 fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2"
                      onClick={() => setActiveAction(null)}
                    >
                      <ArrowLeft size={18} /> Volver al Menú
                    </button>
                  </div>
                ) : activeAction.id === "rm-1" ? (
                  <ClassReportsModal
                    onClose={() => setActiveAction(null)}
                    role={role}
                    userEmail={sessionStorage.getItem("userEmail")}
                    subjectId={eventToEdit.extendedProps.subject}
                    subjectName={eventToEdit.extendedProps.subject_name}
                  />
                ) : activeAction.id === "pr-edit" ? (
                  <>
                    <div className="mb-4 text-start">
                      {/* --- BLOQUES EXISTENTES --- */}
                      {formData.content_blocks && (
                        <div className="mb-4">
                          <label className="form-label text-muted small fw-bold mb-2 text-uppercase">CONTENIDOS GUARDADOS</label>
                          <div className="d-flex flex-column gap-2">
                            {formData.content_blocks.split("\n\n").map((block, idx) => (
                              <div key={idx} className={`p-0 rounded border shadow-sm mb-3 overflow-hidden ${editingIndex === idx ? 'border-primary bg-primary bg-opacity-10' : 'bg-white'}`}>
                                <div className="p-3 d-flex justify-content-between align-items-start">
                                  <div className="small text-dark flex-grow-1" style={{ whiteSpace: "pre-wrap" }}>
                                    {block}
                                  </div>
                                  <div className="d-flex gap-1 ms-3">
                                    <button 
                                      className="btn btn-sm btn-outline-primary border-0 p-1"
                                      onClick={() => {
                                        setNewBlock(block);
                                        setEditingIndex(idx);
                                        setPendingAction("edit");
                                      }}
                                      title="Editar bloque"
                                    >
                                      <Pencil size={15} />
                                    </button>
                                    <button 
                                      className="btn btn-sm btn-outline-danger border-0 p-1"
                                      onClick={() => {
                                        setTargetBlockIndex(idx);
                                        setPendingAction("delete");
                                        setShowScopePrompt(true);
                                      }}
                                      title="Eliminar bloque"
                                    >
                                      <Trash2 size={15} />
                                    </button>
                                  </div>
                                </div>
                                
                                {/* Adjuntos de este bloque */}
                                {existingDocuments.filter(d => d.block_index === idx).map(doc => (
                                  <div key={doc.id} className="px-3 py-2 bg-light border-top d-flex align-items-center justify-content-between">
                                    <a
                                      href={doc.file_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="small text-decoration-none text-dark d-flex align-items-center gap-2 text-truncate"
                                      style={{maxWidth: '80%'}}
                                    >
                                      <Paperclip size={13} className="text-primary" />
                                      {doc.name}
                                    </a>
                                    {doc.propagated_to_series && (
                                      <span className="badge bg-primary-subtle text-primary border border-primary-subtle x-small" style={{fontSize: '0.6rem'}}>
                                        Serie
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* --- OTROS ARCHIVOS (SIN BLOQUE) --- */}
                      {existingDocuments.filter(d => d.block_index === null || d.block_index === undefined).length > 0 && (
                        <div className="mb-4">
                          <label className="form-label text-muted small fw-bold mb-2 text-uppercase d-flex align-items-center gap-2">
                            <Paperclip size={14} /> Otros Archivos Adjuntos
                          </label>
                          <div className="list-group list-group-flush border rounded shadow-sm">
                            {existingDocuments.filter(d => d.block_index === null || d.block_index === undefined).map((doc) => (
                              <div key={doc.id} className="list-group-item d-flex align-items-center justify-content-between py-2 px-3 bg-white">
                                <a
                                  href={doc.file_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="small text-decoration-none text-dark d-flex align-items-center gap-2 text-truncate"
                                  style={{maxWidth: '80%'}}
                                >
                                  <Paperclip size={13} className="text-primary" />
                                  {doc.name}
                                </a>
                                {doc.propagated_to_series && (
                                  <span className="badge bg-primary-subtle text-primary border border-primary-subtle x-small" style={{fontSize: '0.6rem'}}>
                                    Serie
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* --- AGREGAR / EDITAR NUEVO BLOQUE --- */}
                      {!showScopePrompt ? (
                        <div className={`p-3 border rounded shadow-sm mb-4 ${editingIndex !== null ? 'border-primary' : 'border-warning'}`}>
                          <div className="d-flex align-items-center justify-content-between mb-3 fw-bold">
                             <div className={`d-flex align-items-center gap-2 ${editingIndex !== null ? 'text-primary' : 'text-warning'}`}>
                               <FileText size={16} /> {editingIndex !== null ? 'Editando Bloque' : 'Nuevo Bloque de Información'}
                             </div>
                             {editingIndex !== null && (
                               <button 
                                  className="btn btn-link btn-sm text-muted p-0"
                                  onClick={() => {
                                    setEditingIndex(null);
                                    setNewBlock("");
                                    setPendingAction("add");
                                  }}
                               >
                                 Cancelar edición
                               </button>
                             )}
                          </div>
                          <div className="mb-3">
                            <textarea
                              className="form-control"
                              rows="4"
                              placeholder="Escriba contenido, links de video, o notas aquí..."
                              value={newBlock}
                              onChange={(e) => setNewBlock(e.target.value)}
                            ></textarea>
                          </div>
                          
                          {editingIndex === null && (
                            <div className="mb-3">
                               <label className="form-label text-muted small fw-bold">SUBIR ARCHIVO (Opcional)</label>
                               <input
                                 type="file"
                                 className="form-control form-control-sm"
                                 onChange={(e) => setUploadedFiles(Array.from(e.target.files))}
                               />
                            </div>
                          )}
                          
                          <div className="d-flex gap-2">
                            <button
                               type="button"
                               className="btn btn-secondary flex-grow-1"
                               onClick={() => setActiveAction(null)}
                            >
                               Volver
                            </button>
                            <button
                              type="button"
                              className={`btn flex-grow-1 fw-bold ${editingIndex !== null ? 'btn-primary' : 'btn-warning'}`}
                              onClick={() => {
                                if (!newBlock && uploadedFiles.length === 0) return alert("Escriba algo o suba un archivo.");
                                setShowScopePrompt(true);
                              }}
                            >
                              {editingIndex !== null ? 'Actualizar Bloque' : 'Guardar Bloque'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* --- PROMPT DE ALCANCE (SCOPE) --- */
                        <div className="animate-pop-in p-4 bg-warning bg-opacity-10 border border-warning rounded shadow text-center">
                           <h6 className="fw-bold mb-2">¿Confirmar cambios?</h6>
                           <p className="small text-muted mb-4 text-start bg-white p-2 border rounded">
                             {pendingAction === 'delete' ? 'Eliminar bloque permanentemente.' : 'Guardar cambios en el bloque.'}
                             <br/>
                             <span className="fw-bold text-dark">Elija el alcance:</span>
                           </p>
                           <div className="d-grid gap-2">
                             <button 
                                className="btn btn-warning fw-bold" 
                                onClick={() => handleProfessorSubmit("single")}
                                disabled={isUploading}
                             >
                                {isUploading ? "Procesando..." : "Solo para esta clase"}
                             </button>
                             <button 
                                className="btn btn-outline-warning fw-bold" 
                                onClick={() => handleProfessorSubmit("series")}
                                disabled={isUploading}
                             >
                                {isUploading ? "Procesando..." : "Para toda la serie de clases"}
                             </button>
                             <button 
                                className="btn btn-link btn-sm text-muted mt-2" 
                                onClick={() => {
                                  setShowScopePrompt(false);
                                  if (pendingAction === 'delete') {
                                    setTargetBlockIndex(null);
                                    setPendingAction("add");
                                  }
                                }}
                             >
                                Cancelar
                             </button>
                           </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  /* --- FORMULARIO DE COORDINACIÓN (C-1 / CREAR) --- */
                  <form onSubmit={handleFormSubmit}>
                    {eventToEdit && (
                      <div className="alert alert-info py-2 small mb-3">
                        <strong>Nota:</strong> Como Coordinador, solo puedes modificar la programación.
                      </div>
                    )}
                    <div className="mb-3 text-start">
                      <label className="form-label text-muted small fw-bold">
                        DIPLOMATURA
                      </label>
                      <select
                        className="form-select"
                        value={formData.diplomatura}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            diplomatura: e.target.value,
                            materia: "",
                          })
                        }
                        required
                      >
                        <option value="">Seleccione...</option>
                        {diplomaturasFiltradas.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name} ({d.municipio})
                          </option>
                        ))}
                      </select>
                    </div>
                     {formData.diplomatura && (
                       <>
                         <div className="mb-3 text-start">
                           <label className="form-label text-muted small fw-bold">
                             MATERIA
                           </label>
                           <select
                             className="form-select"
                             value={formData.materia}
                            onChange={(e) =>
                               setFormData({ ...formData, materia: e.target.value, teacherEmail: "" })
                            }
                             required
                           >
                             <option value="">Seleccione...</option>
                              {materiasFiltradas.map((m) => (
                                <option key={m.id} value={m.id}>
                                  {m.name}
                                </option>
                              ))}
                           </select>
                         </div>
 
                         <div className="mb-3 text-start">
                           <label className="form-label text-muted small fw-bold">
                             DOCENTE
                           </label>
                           <select
                             className="form-select"
                             value={formData.teacherEmail}
                             onChange={(e) =>
                               setFormData({
                                 ...formData,
                                 teacherEmail: e.target.value,
                               })
                             }
                             required
                           >
                             <option value="">Seleccione Docente...</option>
                             {availableTeachers.map((t) => (
                                <option key={t.id} value={t.email}>
                                 {t.first_name} {t.last_name} ({t.email})
                               </option>
                             ))}
                           </select>
                         </div>

                         <div className="row g-2 mb-3">
                            <div className={eventToEdit ? "col-md-4 text-start" : "col-md-6 text-start"}>
                              {eventToEdit && (
                                <>
                                  <label className="form-label text-muted small fw-bold">
                                    FECHA
                                  </label>
                                  <input
                                    type="date"
                                    className="form-control"
                                    value={formData.fecha}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        fecha: e.target.value,
                                      })
                                    }
                                    required
                                  />
                                </>
                              )}
                            </div>
                            <div className={eventToEdit ? "col-md-4 text-start" : "col-md-6 text-start"}>
                              <label className="form-label text-muted small fw-bold">
                                HORA INICIO
                              </label>
                              <input
                                type="time"
                                className="form-control"
                                value={formData.horaInicio}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    horaInicio: e.target.value,
                                  })
                                }
                                required
                              />
                            </div>
                            <div className={eventToEdit ? "col-md-4 text-start" : "col-md-6 text-start"}>
                              <label className="form-label text-muted small fw-bold">
                                HORA FIN
                              </label>
                              <input
                                type="time"
                                className="form-control"
                                value={formData.horaFin}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    horaFin: e.target.value,
                                  })
                                }
                                required
                              />
                            </div>
                          </div>
  
                          {(formData.series_id || eventToEdit?.extendedProps?.series_id) && (
                            <div className="mb-4 text-start p-3 bg-light rounded border-start border-4 border-warning shadow-sm animate-pop-in">
                              <label className="form-label text-muted small fw-bold d-block mb-2">
                                <Clock size={14} className="me-1" /> ALCANCE DE LA MODIFICACIÓN
                              </label>
                              <p className="x-small text-muted mb-3">Esta clase pertenece a una serie. ¿A qué clases aplicar los cambios?</p>
                              <div className="d-flex gap-4">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="editScope"
                                    id="scope-single"
                                    value="single"
                                    checked={formData.editScope === "single"}
                                    onChange={(e) => setFormData({ ...formData, editScope: e.target.value })}
                                  />
                                  <label className="form-check-label small fw-bold" htmlFor="scope-single">
                                    Solo esta clase
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="editScope"
                                    id="scope-series"
                                    value="series"
                                    checked={formData.editScope === "series"}
                                    onChange={(e) => setFormData({ ...formData, editScope: e.target.value })}
                                  />
                                  <label className="form-check-label small fw-bold" htmlFor="scope-series">
                                    Toda la serie futura
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
 
                         {!eventToEdit && (
                           <>
                             <div className="mb-3 text-start">
                               <label className="form-label text-muted small fw-bold">
                                 REPETIR / FRECUENCIA
                               </label>
                               <select
                                 className="form-select"
                                 value={formData.frecuencia}
                                 onChange={(e) =>
                                   setFormData({
                                     ...formData,
                                     frecuencia: e.target.value,
                                   })
                                 }
                               >
                                 <option value="">Sin Repetición</option>
                                 <option value="diaria">Diaria</option>
                                 <option value="semanal">Semanal</option>
                                 <option value="quincenal">Quincenal</option>
                                 <option value="mensual">Mensual</option>
                               </select>
                             </div>
 
                             {formData.frecuencia && (
                               <div className="mb-3 text-start">
                                 <label className="form-label text-muted small fw-bold">
                                   FECHA FINALIZACIÓN SERIE
                                 </label>
                                 <input
                                   type="date"
                                   className="form-control"
                                   value={formData.fechaFin}
                                   onChange={(e) =>
                                     setFormData({
                                       ...formData,
                                       fechaFin: e.target.value,
                                     })
                                   }
                                   required
                                 />
                               </div>
                             )}
                           </>
                         )}
 
                         <div className="mb-3 text-start">
                           <label className="form-label text-muted small fw-bold">
                             MODALIDAD
                           </label>
                           <div className="d-flex gap-3">
                             <div className="form-check">
                               <input
                                 className="form-check-input"
                                 type="radio"
                                 name="modality"
                                 id="mod-vi"
                                 value="VI"
                                 checked={formData.modality === "VI"}
                                 onChange={(e) =>
                                   setFormData({
                                     ...formData,
                                     modality: e.target.value,
                                   })
                                 }
                               />
                               <label className="form-check-label" htmlFor="mod-vi">
                                 Virtual
                               </label>
                             </div>
                             <div className="form-check">
                               <input
                                 className="form-check-input"
                                 type="radio"
                                 name="modality"
                                 id="mod-pr"
                                 value="PR"
                                 checked={formData.modality === "PR"}
                                 onChange={(e) =>
                                   setFormData({
                                     ...formData,
                                     modality: e.target.value,
                                   })
                                 }
                               />
                               <label className="form-check-label" htmlFor="mod-pr">
                                 Presencial
                               </label>
                             </div>
                           </div>
                         </div>
                       </>
                     )}
                    <div className="d-flex justify-content-between mt-4 gap-2 flex-wrap">
                       <button
                         type="button"
                         className="btn btn-secondary"
                         onClick={() => setActiveAction(null)}
                       >
                         <ArrowLeft size={16} /> Volver
                       </button>

                       {eventToEdit && (
                         <button
                           type="button"
                           className="btn btn-outline-danger"
                           onClick={handleDeleteClass}
                         >
                           Eliminar Clase
                         </button>
                       )}

                       <button
                         type="submit"
                         disabled={isSubmitting}
                         className={`btn ${activeAction.id === "c-1" ? "btn-primary" : "btn-warning"} flex-grow-1`}
                       >
                         {isSubmitting ? "Procesando..." : (eventToEdit ? "Guardar Cambios" : "Programar Clase")}
                       </button>
                     </div>
                  </form>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  </div>

  {showGradeModal && (
    <GradeManagement
      onClose={() => setShowGradeModal(false)}
      role={role}
    />
  )}

  {showMeetPrompt && (
    <MeetNamePrompt
      meetJoinUrl={meetJoinUrl}
      onClose={() => setShowMeetPrompt(false)}
    />
  )}
</div>
);
};

const AttendanceListView = ({ students, onBack, loading }) => {
  return (
    <div className="animate-fade-in">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
          <UserPlus size={20} className="text-primary" /> Lista de Presentes
        </h5>
        <button className="btn btn-sm btn-outline-secondary" onClick={onBack}>
          <ArrowLeft size={16} /> Volver
        </button>
      </div>

      {loading ? (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      ) : students.length === 0 ? (
        <div className="alert alert-info border-0 shadow-sm">
          No hay alumnos inscritos en esta materia.
        </div>
      ) : (
        <div className="table-responsive rounded border shadow-sm">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr className="small text-uppercase text-muted">
                <th className="px-3">Alumno</th>
                <th>DNI</th>
                <th className="text-center">Estado</th>
                <th className="text-center">Tiempo conectado</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr key={s.id} className="align-middle">
                  <td className="px-3">
                    <div className="fw-bold text-dark">{s.full_name}</div>
                    <div className="x-small text-muted">{s.email}</div>
                  </td>
                  <td className="small text-muted">{s.dni}</td>
                  <td className="text-center">
                    {s.is_present ? (
                      <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-2 rounded-pill">
                        Presente
                      </span>
                    ) : (
                      <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2 rounded-pill">
                        Ausente
                      </span>
                    )}
                  </td>
                  <td className="text-center text-muted" style={{ fontSize: '1.575em' }}>
                    {s.connected_minutes ? `${s.connected_minutes} min` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="mt-4 p-3 bg-light rounded border x-small text-muted">
        <Clock size={12} className="me-1" /> Solo se muestran los alumnos que marcaron asistencia en esta fecha específica.
      </div>
    </div>
  );
};

export default DailyMenu;
